import api, { BACKEND } from "@/lib/api";

// Curated tea-trade vocabulary for the Character & buyer fit section (free text always allowed).
export const CHARACTER_OPTIONS = {
  body: ["Light", "Light-Medium", "Medium", "Medium-Full", "Full", "Heavy"],
  strength: ["Mild", "Medium", "Balanced", "Strong", "Very strong", "Brisk & strong"],
  briskness: ["Low", "Medium", "Medium-High", "High", "Very brisk", "Soft"],
  aroma: ["Warm malty", "Malty", "Sweet malty", "Fresh", "Floral", "Fruity", "Biscuity", "Woody", "Smoky", "Mild"],
};
export const BUYER_FIT_OPTIONS = ["Retail packs", "Milk-tea blending", "Tea stalls & hotels", "Packet tea", "Loose tea sales", "Catering supply",
  "Wholesale blending", "Premium retail", "Masala chai base", "Household consumption", "Hotels & restaurants", "Budget blends", "Strong milk tea",
  "Vending & canteens", "Export quality"];

export const SENSORY = [
  ["dry_leaf", "Dry leaf"],
  ["liquor", "Liquor"],
  ["wet_leaf", "Wet leaf"],
  ["taste_aroma", "Taste & aroma"],
];
export const CUP = [
  ["colour", "Colour"],
  ["body", "Body"],
  ["briskness", "Briskness"],
  ["aroma", "Aroma"],
  ["astringency", "Astringency"],
  ["sweetness", "Sweetness"],
  ["mouthfeel", "Mouthfeel"],
  ["finish", "Finish"],
];
export const STANDARD_GRADES = ["BP", "BOP", "BOPSM", "BOPL", "OF", "PF", "PD", "DUST"];
export const ORIGINS = ["Assam", "Dooars", "Terai", "Darjeeling", "Nilgiri", "Other"];
export const TEA_TYPES = ["CTC", "Orthodox", "Green", "Dust", "Other"];

export const STATUS_META = {
  available: { label: "Available", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  sold: { label: "SOLD", cls: "bg-red-100 text-red-800 border-red-300" },
  archived: { label: "Archived", cls: "bg-stone-200 text-stone-700 border-stone-400" },
};

export const emptyProduct = (taxRate = "5") => ({
  name: "", manufacturer: "", origin: "", tea_type: "", grade: "", description: "", invoice_no: "", lot_no: "", date: "",
  price_per_kg: "", tax_rate: taxRate, commercial_terms: "", body: "", strength: "", briskness: "", aroma: "", buyer_fit: [],
  overall_rating: null, overall_rating_source: null, overall_text: "", milk_score: null, milk_text: "", value_score: null, value_text: "",
  sensory: Object.fromEntries(SENSORY.map(([k]) => [k, { score: null, text: "" }])),
  cup: Object.fromEntries(CUP.map(([k]) => [k, { score: null, text: "" }])),
  verdict: "", attribution_heading: "", tasting_confirmed: false, photo: null, field_sources: {}, ai: null, ai_scoring: null, status: "available",
});

export const fmtMoney = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  if (!isFinite(n) || v === "" || v == null) return "—";
  return Number.isInteger(n) ? `₹${n.toLocaleString("en-IN")}` : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const fmtScore = (v) => (v == null || v === "" ? "—" : Number(v).toFixed(1));
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export async function uploadPhoto(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/studio/photos", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
  });
  return data;
}

export async function uploadCover(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/studio/covers", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export async function previewPng(product, orientation, catalogueNumber) {
  const r = await api.post("/studio/preview", { product, orientation, catalogue_number: catalogueNumber }, { responseType: "blob" });
  let overflow = [];
  try { overflow = JSON.parse(r.headers["x-overflow"] || "[]"); } catch (e) { overflow = []; }
  return { url: URL.createObjectURL(r.data), overflow };
}

const nameFrom = (r, fallback) => {
  const m = /filename="?([^";]+)"?/.exec(r.headers["content-disposition"] || "");
  return m ? m[1] : fallback;
};

export async function fetchPdf(path, params, fallbackName = "catalogue.pdf") {
  const r = await api.get(path, { params, responseType: "blob" });
  const file = new File([r.data], nameFrom(r, fallbackName), { type: "application/pdf" });
  return { file, overflow: Number(r.headers["x-overflow-count"] || 0) };
}

export function saveFile(file) {
  const u = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = u;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 10000);
}

export function printFile(file) {
  const u = URL.createObjectURL(file);
  const w = window.open(u, "_blank");
  if (!w) saveFile(file);
}

export const fileSrc = (u) => (u ? `${BACKEND}${u}` : null);

export const variantLabel = (orientation, size) => (orientation === "portrait" ? "Portrait A4" : size === "a4" ? "Landscape · A4 fit" : "Landscape · original");

// Download a stored file (cookie-authenticated /api/files URL) under its proper filename
export async function downloadStored(url, filename) {
  const r = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
  saveFile(new File([r.data], filename, { type: "application/pdf" }));
}
